import { get, isEmpty, toUpper } from "lodash"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { Creators as ProfileCreators } from "model/profileModal"
import { getTickerMicAndId, treatAsFactor } from "helpers/securities"

export function getIsNoLongerHeld(position, originalComposition, originalPerformance, trades) {
  // If position is explicitly marked as no longer held, it's "no longer held"
  // This value is determined in the gatherer, and is unrelated to trading
  if (position.isNoLongerHeld) {
    return true
  }

  // If position is explicitly marked as not held, it's not "no longer held"
  if (position.notHeld) {
    return false
  }

  const hasTrades = !isEmpty(trades)
  const originalExposure = originalComposition?.positionSetEconomicExposure
  const currentExposure = get(position, "values.composition.positionSetEconomicExposure")
  const rebalancedExposure = get(position, "values.composition-rebalanced.positionSetEconomicExposure")

  // If there was original performance, but no original exposure and composition, it is no longer held
  if (originalPerformance && !originalExposure && !originalComposition) {
    return true
  }

  // If there was no original exposure, and no performance values it was never held
  if (!originalExposure && !originalPerformance) {
    return false
  }

  // Case 1: No trades - check if current exposure is missing
  if (!hasTrades && !currentExposure) {
    return true
  }

  // Case 2: Has trades - check if rebalanced exposure is missing (completely traded away)
  if (hasTrades && !rebalancedExposure) {
    return true
  }

  return false
}

/**
 * Creates an action function to open the appropriate profile/composite for a position
 * @param {Object} position - Security or position object
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Function|null} Action function or null if conditions not met
 */
export function createPositionActionFn(position, dispatch) {
  if (position?.isFactor) {
    return () => {
      dispatch(ProfileCreators.openFactorProfile(position.id))
    }
  }

  if (position.isUnmodeled || position.children || position.action || (position?.values && !position.values.composition)) {
    return null
  }

  // Get position properties - handle both contributor and position formats
  const positionData = position.values?.composition
    ? { ...position, ...position.values.composition, id: position.values.composition.identifier }
    : position

  const { assetClass, id } = positionData

  const fallbacks = getTickerMicAndId(assetClass, id, positionData.ticker, positionData.mic)
  const ticker = positionData.ticker || positionData.identifier || fallbacks.ticker
  const mic = positionData.mic || fallbacks.mic

  return () => {
    if (treatAsFactor(assetClass)) {
      dispatch(ProfileCreators.openFactorProfile(id))
    } else if (assetClass === "index") {
      dispatch(CompositeCreators.openCompositeSecurity({ ...positionData, id, ticker, type: assetClass }))
    } else {
      dispatch(ProfileCreators.openSecurityProfile2(assetClass, id, ticker, mic))
    }
  }
}

/**
 * Get an array of index position ids and types from instruments metadata
 * @param {Array} instrumentsMetadata
 * @returns {Array<{id: string, type: string}>}
 */
export function getIndexPositionSets(instrumentsMetadata) {
  return instrumentsMetadata
    .filter(instrumentMetadata => toUpper(instrumentMetadata?.type) === "INDEX")
    .map(({ ticker, type }) => ({
      id: ticker,
      type,
    }))
}
